import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { DailyStats } from '@/types/feedback';

/**
 * Portfolio-wide daily response volume across all accounts. Uses recharts (already
 * a dependency); teal brand fill. The daily series is the summed DailyBreakdown.
 */
export function PortfolioTrend({ daily }: { daily: DailyStats[] }) {
  const data = daily.map((d) => ({
    date: d.date,
    responses: d.count,
    net: d.count > 0 ? Math.round(((d.thumbsUp - d.thumbsDown) / d.count) * 100) : 0,
  }));

  const hasData = data.some((d) => d.responses > 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Portfolio activity</CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No responses in this period</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
              <defs>
                <linearGradient id="portfolioVolume" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--chart-1, #14b8a6)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="var(--chart-1, #14b8a6)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatTick}
                tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                minTickGap={24}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                width={44}
                tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                labelFormatter={(v) => String(v)}
                formatter={(value) => [value as number, 'Responses']}
                contentStyle={{
                  background: 'var(--popover)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 12,
                  color: 'var(--popover-foreground)',
                }}
              />
              <Area
                type="monotone"
                dataKey="responses"
                stroke="var(--chart-1, #14b8a6)"
                strokeWidth={2}
                fill="url(#portfolioVolume)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

// "2026-07-31" -> "31 Jul"
function formatTick(date: string): string {
  const d = new Date(date + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}
